"use client"
import styled from "@emotion/styled"
import { Typography } from "@mui/material"
import { useLayoutEffect, useRef, useState } from "react"

const MAX_LINES = 2
const READ_MORE_ATTR = "data-read-more"
const TRAILING_PUNCTUATION = /[\s,–\-:;]+$/

const Wrapper = styled.div`
  position: relative;
  width: 100%;
`

const Text = styled(Typography)`
  word-break: break-word;
  & p {
    margin: 0;
  }
  & [${READ_MORE_ATTR}] {
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;
    color: inherit;
    font: inherit;
    font-style: normal;
    font-weight: 500;
    text-decoration: underline;
    /* Ploni glyphs dip ~0.16em below the baseline; the underline starts right under them */
    text-underline-offset: 0.16em;
    text-decoration-skip-ink: auto;
  }
` as typeof Typography

// Before the first measurement, keep the server-rendered text from pushing the cars down
const PendingText = styled(Text)`
  max-height: ${MAX_LINES}lh;
  overflow: hidden;
` as typeof Typography

const Measurer = styled(Text)`
  position: absolute;
  inset: 0 0 auto 0;
  visibility: hidden;
  pointer-events: none;
` as typeof Typography

// <template> parses inertly: no images load and no handlers run while we measure
const parseHtml = (html: string): HTMLTemplateElement => {
  const template = document.createElement("template")
  template.innerHTML = html
  return template
}

const createReadMoreButton = (): HTMLButtonElement => {
  const button = document.createElement("button")
  button.type = "button"
  button.setAttribute(READ_MORE_ATTR, "")
  button.textContent = "קרא עוד"
  return button
}

const removeFollowingNodes = (node: Node, root: Node): void => {
  let current: Node | null = node
  while (current && current !== root) {
    while (current.nextSibling) {
      current.parentNode?.removeChild(current.nextSibling)
    }
    current = current.parentNode
  }
}

// Keeps the first `limit` characters of text (formatting intact) and appends "read more" inline
const buildCollapsedHtml = (html: string, limit: number): string => {
  const template = parseHtml(html)
  const root = template.content
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  let remaining = limit
  let node = walker.nextNode()
  while (node) {
    const text = node.textContent ?? ""
    if (text.length >= remaining) {
      node.textContent = text.slice(0, remaining).replace(TRAILING_PUNCTUATION, "") + " "
      removeFollowingNodes(node, root)
      node.parentNode?.insertBefore(createReadMoreButton(), node.nextSibling)
      return template.innerHTML
    }
    remaining -= text.length
    node = walker.nextNode()
  }
  return template.innerHTML
}

const getWordEnds = (html: string): number[] => {
  const text = parseHtml(html).content.textContent ?? ""
  const ends: number[] = []
  for (let i = 1; i < text.length; i++) {
    if (/\s/.test(text[i]) && !/\s/.test(text[i - 1])) ends.push(i)
  }
  return ends
}

type Props = {
  html: string
  variant: "body1" | "body2"
}

const SearchIntro = ({ html, variant }: Props): React.ReactElement => {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const measurerRef = useRef<HTMLDivElement>(null)
  const [collapsedHtml, setCollapsedHtml] = useState<string | null>(null)
  const [isMeasured, setIsMeasured] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)

  useLayoutEffect(() => {
    const wrapper = wrapperRef.current
    const measurer = measurerRef.current
    if (!wrapper || !measurer || isExpanded) return

    const fits = (content: string, maxHeight: number): boolean => {
      measurer.innerHTML = content
      return measurer.offsetHeight <= maxHeight
    }

    const measure = (): void => {
      // Hidden by a Desktop/Mobile wrapper; measure again once it becomes visible
      if (!wrapper.offsetWidth) return

      measurer.innerHTML = "א"
      const maxHeight = measurer.offsetHeight * MAX_LINES + 1

      if (fits(html, maxHeight)) {
        setCollapsedHtml(null)
      } else {
        const wordEnds = getWordEnds(html)
        let low = 0
        let high = wordEnds.length - 1
        let best = buildCollapsedHtml(html, 0)
        while (low <= high) {
          const mid = Math.floor((low + high) / 2)
          const candidate = buildCollapsedHtml(html, wordEnds[mid])
          if (fits(candidate, maxHeight)) {
            best = candidate
            low = mid + 1
          } else {
            high = mid - 1
          }
        }
        setCollapsedHtml(best)
      }
      measurer.innerHTML = ""
      setIsMeasured(true)
    }

    measure()
    let lastWidth = wrapper.offsetWidth
    const observer = new ResizeObserver(() => {
      if (wrapper.offsetWidth === lastWidth) return
      lastWidth = wrapper.offsetWidth
      measure()
    })
    observer.observe(wrapper)
    document.fonts?.ready.then(measure)
    return () => observer.disconnect()
  }, [html, isExpanded])

  const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
    if ((event.target as HTMLElement).closest(`[${READ_MORE_ATTR}]`)) {
      setIsExpanded(true)
    }
  }

  const VisibleText = isMeasured || isExpanded ? Text : PendingText
  const visibleHtml = !isExpanded && collapsedHtml ? collapsedHtml : html

  return (
    <Wrapper ref={wrapperRef}>
      <VisibleText
        variant={variant}
        component="div"
        onClick={handleClick}
        dangerouslySetInnerHTML={{ __html: visibleHtml }}
      />
      {!isExpanded && <Measurer ref={measurerRef} variant={variant} component="div" aria-hidden />}
    </Wrapper>
  )
}

export default SearchIntro
